import type { RichEditorPreset } from '@odo/shared-ui';
import { PLAIN_TAIL, blankLine, bulletList, docOf, paragraph } from './docBuilders';
import { filled, type VcaNoteFields } from './noteFields';

export function dynamicRetentionNotesPreset(fields: VcaNoteFields): RichEditorPreset {
    const f = filled(fields);
    return {
        id: 'dynamic-retention',
        label: 'Dynamic retention',
        description: 'Date, doctor and current aligner filled in; edit the rest.',
        doc: docOf([
            paragraph([
                [{ bold: 'Date Posted:' }, ` ${f.date}`],
                [`Good Morning/Afternoon Dr. ${f.doctor},`]
            ]),
            blankLine(),
            paragraph([
                [
                    'The patient may now be scheduled for either ',
                    { bold: 'Dynamic Retention' },
                    ' or a ',
                    { bold: 'Refinement Evaluation.' },
                    PLAIN_TAIL
                ],
                [{ bold: 'Aligner Status' }]
            ]),
            bulletList([[`Current aligner: ${f.aligner}`], ['Aligners [#–#] are passive.']]),
            paragraph([[{ bold: 'Ortho Notes:' }]]),
            bulletList([
                [
                    'If preferred, traditional retainers may be used until the patient is ready for Phase 2. Please scan the full palate if a Hawley retainer is wanted, and let us know if you need assistance with retainer ordering. This option may have limitations and possible additional costs if multiple retainers are required. Dynamic Retention is strongly recommended following Phase 1 completion.'
                ],
                [
                    'If you and the patient are satisfied with treatment progress and would like to proceed with Dynamic Retention, please follow the instructions below.'
                ],
                ['Please also evaluate the need for updated radiographs at the next visit.'],
                [
                    'If a refinement is needed instead, please select “Reject and Continue Treatment” and provide the reasoning.'
                ]
            ]),
            paragraph([[{ bold: 'Dynamic Retention:' }]]),
            blankLine(),
            bulletList([
                ['For Phase 1 patients only'],
                ['Retainers include small programmed movements for mild additional expansion'],
                ['Worn at night only and changed every 30 days'],
                ['No additional charge (considered a refinement)'],
                ['Includes 14 retainers to help prevent relapse due to loss or breakage'],
                ['Pontics may be added if needed for eruption management']
            ]),
            blankLine(),
            paragraph([[{ bold: 'Dynamic Retention Protocol:' }]]),
            blankLine(),
            bulletList([
                ['DentalMonitoring should NOT be enabled'],
                [
                    'Review the “Phase 1 Doctor Facing” and “Phase 1 Patient Facing” documents under Resources > Help Center'
                ],
                [
                    {
                        bold: 'Please note that updated records while the patient is in Dynamic Retention should not be sent to ODO for review unless the patient is ready for Phase II treatment.'
                    },
                    PLAIN_TAIL
                ],
                ['Attachment removal is optional, or we can remove them virtually']
            ]),
            blankLine(),
            paragraph([[{ bold: 'DM Notifications' }]]),
            bulletList([
                ['DM notified the following. Please evaluate during the appointment:'],
                ['[Insert concern]'],
                ['[Insert concern]']
            ]),
            paragraph([['Thank you.']])
        ])
    };
}
